import {
    addMonths,
    addWeeks,
    addYears,
    differenceInCalendarMonths,
    differenceInMonths,
    differenceInWeeks,
    differenceInYears,
    isAfter,
    isBefore,
    isWithinInterval,
} from 'date-fns';
import { useMemo } from 'react';

import { FormattedOrganizationHoliday } from '@/domain/myOrganization';
import { DatePickerRange } from '@/domain/requestsPage';
import { RECURRING_TYPE } from '@/utils/enums/common';

const repeatHolidayOverTimePeriod = (
    holiday: FormattedOrganizationHoliday,
    range: DatePickerRange,
    add: typeof addYears | typeof addMonths | typeof addWeeks,
    diff: typeof differenceInYears | typeof differenceInMonths | typeof differenceInWeeks
) => {
    if (!range.startDate || !range.endDate) {
        return [];
    }
    const dateDiff = diff(range.startDate, holiday.formattedDate);
    const startDate = dateDiff < 0 ? holiday.formattedDate : add(holiday.formattedDate, dateDiff);
    const repeatTimes = diff(range.endDate, dateDiff <= 0 ? holiday.formattedDate : range.startDate) + 1; // inclusive
    if (repeatTimes <= 0) {
        return [];
    }
    return Array.from(Array(repeatTimes).keys())
        .map(period => {
            const date = add(startDate, period);
            if (!isWithinRange(date, range)) {
                return undefined;
            }
            return {
                ...holiday,
                date,
                formattedDate: date,
            };
        })
        .filter(Boolean);
};
const isWithinRange = (date: Date, { startDate: start, endDate: end }: DatePickerRange) => {
    if (!start || !end) {
        return false;
    }
    return isWithinInterval(date, {
        start,
        end,
    });
};

const getHolidaysInRange = (holidays, range, addHandler, diffHandler) =>
    holidays.map(holiday => repeatHolidayOverTimePeriod(holiday, range, addHandler, diffHandler)).flat();

export const useHolidays = (holidays: FormattedOrganizationHoliday[], calendarRange: DatePickerRange) => {
    const filterHolidaysByRecurringType = type => holidays?.filter(holiday => holiday.recurringType === type) ?? [];

    const yearlyHolidaysInRange = getHolidaysInRange(
        filterHolidaysByRecurringType(RECURRING_TYPE.YEARLY),
        calendarRange,
        addYears,
        differenceInYears
    );
    const monthlyHolidaysInRange = getHolidaysInRange(
        filterHolidaysByRecurringType(RECURRING_TYPE.MONTHLY),
        calendarRange,
        addMonths,
        differenceInCalendarMonths
    );
    const weeklyHolidaysInRange = getHolidaysInRange(
        filterHolidaysByRecurringType(RECURRING_TYPE.WEEKLY),
        calendarRange,
        addWeeks,
        differenceInWeeks
    );
    const nonRecurringHolidays = filterHolidaysByRecurringType(RECURRING_TYPE.NONE).filter(holiday =>
        isWithinRange(holiday.formattedDate, calendarRange)
    );

    const holidaysInRange = [
        ...yearlyHolidaysInRange,
        ...monthlyHolidaysInRange,
        ...weeklyHolidaysInRange,
        ...nonRecurringHolidays,
    ].sort((a, b) => (isBefore(a, b) ? -1 : 1));

    return useMemo(() => holidaysInRange?.map(({ formattedDate }) => formattedDate) ?? [], [holidaysInRange]);
};
